import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { FilterPopover } from '@/components/ProjectsTable/FilterPopover';
import { ReaderTagsFacetFilterPopover } from '@/components/ReadersTable/ReaderTagsFacetFilterPopover';
import type { Reader } from '@/api/client';
import { FilterAltIcon } from '@/components/ui/Icons';
import { READERS_CATALOG_COLUMN_FILTER_TYPES } from '@/lib/tableFilters/readersCatalogColumnFilterTypes';
import { useReadersCatalogFilters } from '@/contexts/ReadersCatalogFiltersContext';
import { readersCatalogFilterColumnTitle } from '@/utils/readersCatalogFilterTitles';
import { getReadersCatalogOptionsForColumn } from '@/utils/readersCatalogFilterOptions';
import type { FilterColumnType } from '@/lib/tableFilters';
import { isRuleEmpty } from '@/lib/tableFilters/filterRuleEmpty';
import '@/components/ProjectsTable/HeaderCellWithWidth.css';

export interface ReadersCatalogHeaderFilterCellProps {
  columnId: string;
  columnTitle: string;
  readers: Reader[];
}

export function ReadersCatalogHeaderFilterCell({
  columnId,
  columnTitle,
  readers,
}: ReadersCatalogHeaderFilterCellProps) {
  const { filtersEnabled, filters, setFilter } = useReadersCatalogFilters();

  const cellRef = useRef<HTMLDivElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const currentRule = filters[columnId] ?? null;
  const isTagsColumn = columnId === 'tags';
  const filterColumnType: FilterColumnType | undefined = isTagsColumn
    ? undefined
    : READERS_CATALOG_COLUMN_FILTER_TYPES[columnId];

  const showFilterChrome = Boolean(isTagsColumn || filterColumnType);

  const hasActiveFilter = currentRule !== null && !isRuleEmpty(currentRule);

  const getOptions = useMemo(
    () => (col: string, ft: FilterColumnType) => getReadersCatalogOptionsForColumn(col, ft, readers),
    [readers]
  );

  const detailTitle =
    readersCatalogFilterColumnTitle(columnId) !== columnId
      ? readersCatalogFilterColumnTitle(columnId)
      : columnTitle.trim() !== ''
        ? columnTitle
        : readersCatalogFilterColumnTitle(columnId);

  const handleClose = () => {
    setFilterOpen(false);
    setHovered(false);
  };

  const showFilterBtn = showFilterChrome && (filtersEnabled || hasActiveFilter);

  const openPopover = () => setFilterOpen(true);

  const tagsFacetRule = currentRule?.type === 'readerTagsFacet' ? currentRule : null;

  const standardRule =
    columnId !== 'tags' && currentRule?.type === 'readerTagsFacet' ? null : currentRule;

  return (
    <>
      <div
        ref={cellRef}
        className="header-cell-with-width readers-catalog-header-filter-cell"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span className="projects-table__header-cell-text">{columnTitle}</span>
        <div
          className={clsx(
            'header-cell-with-width__controls',
            (hovered || filterOpen || hasActiveFilter) && 'header-cell-with-width__controls_visible'
          )}
        >
          {showFilterBtn && (
            <button
              type="button"
              className={`header-cell-with-width__btn header-cell-with-width__btn_filter${hasActiveFilter ? ' header-cell-with-width__btn_filter-active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openPopover();
              }}
              title="Фильтр"
              aria-label="Фильтр"
            >
              <FilterAltIcon size={12} />
              {hasActiveFilter && <span className="header-cell-with-width__filter-dot" />}
            </button>
          )}
        </div>
      </div>

      {!isTagsColumn && filterColumnType && (
        <FilterPopover
          columnId={columnId}
          filterType={filterColumnType}
          currentRule={standardRule}
          onApply={(cid, rule) => setFilter(cid, rule)}
          triggerRef={cellRef}
          isOpen={filterOpen}
          onClose={handleClose}
          getOptions={getOptions}
          headerLabel={detailTitle}
        />
      )}
      {isTagsColumn && (
        <ReaderTagsFacetFilterPopover
          currentRule={tagsFacetRule}
          onApply={(rule) => setFilter('tags', rule)}
          triggerRef={cellRef}
          isOpen={filterOpen}
          onClose={handleClose}
        />
      )}
    </>
  );
}
